import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Accumulation Phase Chart - Stacked Area Chart by Account
 * Shows growth of retirement accounts from current age to retirement
 */
export default function AccumulationChart({ data, accounts, currencySymbol = "$" }) {
  if (!data || !data.projections || data.projections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Accumulation Phase</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Transform data for stacked area chart
  const chartData = data.projections.map(yearData => {
    const point = {
      year: yearData.year,
      age: yearData.age,
      total: yearData.totalBalance
    };

    // Add each account's balance
    Object.keys(yearData.accounts).forEach(accountId => {
      const account = yearData.accounts[accountId];
      point[accountId] = account.balance;
    });

    return point;
  });

  // Generate colors for each account
  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#84cc16'  // lime
  ];

  const accountIds = Object.keys(data.projections[0]?.accounts || {});

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the projection data by matching the age (label)
      const yearData = data.projections.find(p => p.age === label);
      if (!yearData) return null;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold">Age {yearData.age} (Year {yearData.year})</p>
          <p className="text-sm text-gray-600 mb-2">
            Total: {currencySymbol}{yearData.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </p>
          <div className="space-y-1">
            {payload.reverse().map((entry, index) => {
              if (entry.dataKey === 'total') return null;
              const accountData = yearData.accounts[entry.dataKey];
              if (!accountData) return null;
              return (
                <div key={index} className="flex justify-between gap-4 text-sm">
                  <span style={{ color: entry.color }}>{accountData.name}:</span>
                  <span className="font-medium">
                    {currencySymbol}{accountData.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accumulation Phase</CardTitle>
        <CardDescription>
          Projected growth from age {data.projections[0]?.age} to {data.projections[data.projections.length - 1]?.age}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="age"
                label={{ value: 'Age', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft' }}
                tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                width={100}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {accountIds.map((accountId, index) => {
                const account = data.projections[0]?.accounts[accountId];
                return (
                  <Area
                    key={accountId}
                    type="monotone"
                    dataKey={accountId}
                    stackId="1"
                    stroke={colors[index % colors.length]}
                    fill={colors[index % colors.length]}
                    fillOpacity={0.6}
                    name={account?.name || accountId}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          <div>
            <div className="text-sm text-muted-foreground">Starting Balance</div>
            <div className="text-2xl font-bold">
              {currencySymbol}{data.projections[0]?.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Balance at Retirement</div>
            <div className="text-2xl font-bold text-green-600">
              {currencySymbol}{data.totalAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Total Growth</div>
            <div className="text-2xl font-bold text-blue-600">
              {currencySymbol}{(data.totalAtRetirement - data.projections[0]?.totalBalance).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>

        {/* Account Breakdown at Retirement */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-semibold mb-3">Account Balances at Retirement</h4>
          <div className="space-y-2">
            {accountIds.map((accountId, index) => {
              const account = data.projections[data.projections.length - 1]?.accounts[accountId];
              const percentage = (account.balance / data.totalAtRetirement) * 100;
              return (
                <div key={accountId} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: colors[index % colors.length] }}
                  />
                  <div className="flex-1 flex justify-between">
                    <span className="text-sm">{account.name}</span>
                    <span className="text-sm font-medium">
                      {currencySymbol}{account.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
